import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { coarsePointerOnly, hoverOnly } from '../../theme/mixins';

export const legendListStyles: SxProps<Theme> = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.5,
    listStyle: 'none',
    m: 0,
    mb: 1,
    p: 0,
};

export const legendItemStyles: SxProps<Theme> = {
    display: 'inline-flex',
};

/**
 * A real button, so it is tab-reachable and Enter/Space-activatable without a keyboard
 * handler of our own. Deliberately quiet: it is a control over the chart, not a call to
 * action.
 *
 * Quiet is not the same as small. At `py: 0.25` this draws a 26px-tall target, which is
 * fine for a cursor and about half what a fingertip needs -- and these sit in a wrapped
 * row of neighbours, so a miss does not do nothing, it switches off the wrong therapist.
 * The floor is raised only where the pointer is coarse, since 44px would leave the
 * desktop legend looking like a row of tags.
 */
export const legendButtonStyles = (isHidden: boolean): SxProps<Theme> => ({
    px: 1,
    py: 0.25,
    minWidth: 0,
    // `px`, not `paddingLeft: 12`. Both go through sx's spacing scale, so the bare 12 meant
    // 12 * 8px = 96px of padding a side: on a coarse pointer -- and only there, which is why
    // narrowing a desktop window never showed it -- every entry grew past the width of the
    // card and the legend collapsed into one item per line. `minHeight` sits on the sizing
    // scale instead, where a bare 44 really is 44px, which is what made the mistake look
    // like it worked.
    ...coarsePointerOnly({ minHeight: 44, px: 1.5 }),
    borderRadius: 999,
    textTransform: 'none',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: 'text.secondary',
    // Three channels rather than one, so a switched-off series still reads as switched off
    // in greyscale, in print, and to somebody who cannot separate the hues.
    opacity: isHidden ? 0.45 : 1,
    textDecoration: isHidden ? 'line-through' : 'none',
    ...hoverOnly({
        backgroundColor: 'rgba(16,24,40,0.04)',
        // Restated because MuiButton's own hover clears it, and a hidden series must keep
        // reading as hidden while the pointer is on it.
        textDecoration: isHidden ? 'line-through' : 'none',
    }),
});

/**
 * Filled when the series is drawn, a hollow ring when it is not -- the third channel, and
 * the one that survives being photocopied. The border keeps a pale hue visible on white.
 */
export const legendSwatchStyles = (color: string, isHidden: boolean): SxProps<Theme> => ({
    display: 'inline-block',
    width: 12,
    height: 12,
    mr: 0.75,
    borderRadius: '3px',
    flexShrink: 0,
    backgroundColor: isHidden ? 'transparent' : color,
    border: isHidden ? `2px solid ${color}` : '1px solid rgba(16,24,40,0.15)',
});
